import { S3Level } from 'API';
import { useEffect, useState } from 'react';
import clsx from 'clsx';
import PictureService from 'services/picture';
import StorageService from 'services/storage';
import {
  Avatar, AvatarProps,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import { Picture, S3Object } from 'models';
import { getCacheWithExpiration } from 'shared/utilities';

interface Props extends AvatarProps {
  picture?: S3AvatarSrc;
  shadow?: boolean;
  srcOverride?: string | Blob;
  width?: number;
  canHaveStroke?: boolean;
  archived?: boolean;
}

/**
 * This component accepts a Picture or S3Object and retrieves
 * an image from S3, preferring a square crop if available.
 * However, there are times (such as when editing a photo) where
 * we want to bypass S3 and send this component one of three things:
 * a dataURL of an image, a URL, or a Blob.
 */

const S3Avatar: React.FC<Props> = (props: Props) => {
  const {
    alt,
    children,
    className,
    picture,
    shadow,
    archived,
    canHaveStroke,
    srcOverride,
    width, ...imgProps
  } = props;
  const [src, setSrc] = useState('');
  useEffect(() => {
    let ignore = false;

    const getImage = async () => {
      if (!picture) {
        return;
      }

      let s3Object: S3Object;

      if ((picture as S3Object).key) {
        s3Object = picture as S3Object;
      } else {
        s3Object = PictureService.targetPictureSize(
          picture as Picture,
          width || 128,
          true,
        );
      }

      const { key, level, identityId } = s3Object;
      try {
        const response = await getCacheWithExpiration(
          `${key}${identityId}${level}`,
          async () => StorageService.getImage(
            key,
            (level || S3Level.PUBLIC) as S3Level,
            identityId,
          ),
          (15 * 60 - 10) * 1000, // 15 minutes minus a hair, in ms
        );

        if (typeof response === 'string' && !ignore) {
          setSrc(response);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err);
      }
    };

    if ((picture?.key || picture?.original?.key) && !srcOverride) {
      getImage();
    } else if (srcOverride) {
      if (typeof srcOverride === 'object') {
        // It's a blob
        setSrc(URL.createObjectURL(srcOverride));
      } else {
        // It's a string
        setSrc(srcOverride);
      }
    } else {
      setSrc('');
    }

    return () => { ignore = true; };
  }, [picture, srcOverride]);

  return (
    <Avatar
      src={src}
      alt={alt}
      className={clsx(className, shadow && 'avatar--shadow')}
      sx={(theme: Theme) => canHaveStroke ? {
        borderWidth: 3,
        borderStyle: 'solid',
        borderColor: archived ? theme.palette.primary4 : theme.palette.primary1,
      } : {}}
      {...imgProps}
    >
      {children}
    </Avatar>
  );
};

export default S3Avatar;
